import React, { useState } from 'react';
import { XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { useVoidContractMutation } from '@/apis/extended/contractApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface VoidContractButtonProps {
  contractId: string;
  voidedBy: 'agent' | 'seller' | 'buyer';
  onVoidSuccess?: () => void;
  disabled?: boolean;
  buttonText?: string;
  variant?: 'default' | 'destructive' | 'outline';
  contractStatus?: string;
  isRatified?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export const VoidContractButton: React.FC<VoidContractButtonProps> = ({
  contractId,
  voidedBy,
  onVoidSuccess,
  disabled = false,
  buttonText,
  variant = 'destructive',
  contractStatus,
  isRatified = false,
  size = 'default',
  className = '',
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [voidContract, { isLoading }] = useVoidContractMutation();

  // Prevent voiding ratified contracts
  const isDisabled =
    disabled ||
    isRatified ||
    contractStatus === 'ratified' ||
    contractStatus === 'voided';

  const getButtonText = () => {
    if (buttonText) return buttonText;
    return voidedBy === 'buyer' ? 'Withdraw Offer' : 'Reject Offer';
  };

  const getDialogTitle = () => {
    return voidedBy === 'buyer'
      ? 'Withdraw Your Offer'
      : 'Reject Contract Offer';
  };

  const getDialogDescription = () => {
    if (voidedBy === 'buyer') {
      return 'Are you sure you want to withdraw your offer? The seller and agent will be notified immediately.';
    }
    return 'Are you sure you want to reject this offer? The buyer will be notified immediately via email and in-app notification.';
  };

  const getSuccessMessage = () => {
    if (voidedBy === 'buyer') {
      return 'Offer withdrawn successfully. Seller and agent have been notified.';
    }
    return 'Contract voided successfully. Buyer has been notified.';
  };

  const handleVoid = async () => {
    try {
      await voidContract({
        contractId,
        voidedReason: reason,
        voidedBy,
      }).unwrap();

      toast.success(getSuccessMessage());
      setShowDialog(false);
      setReason('');
      onVoidSuccess?.();
    } catch (error: any) {
      console.error('Void contract error:', error);
      const errorMessage =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        'Failed to void contract';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => {
          if (isRatified || contractStatus === 'ratified') {
            toast.error('Cannot void a ratified contract');
            return;
          }
          if (contractStatus === 'voided') {
            toast.error('Contract is already voided');
            return;
          }
          setShowDialog(true);
        }}
        disabled={isDisabled}
        className={`gap-2 ${className}`}
        title={
          isRatified || contractStatus === 'ratified'
            ? 'Cannot void ratified contracts'
            : ''
        }
      >
        <XCircle className="h-4 w-4" />
        {getButtonText()}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>{getDialogDescription()}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason (Optional)</label>
              <Textarea
                placeholder="Provide a reason for rejection (optional)..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoid}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : getButtonText()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
